import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { eventAPI } from '../../actions/imsServer';
import { formatDateTimeDisplay } from '../../utils/base';
import ContentArea from '../../components/contentArea';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { API_PAGE_SIZE } from '../../constants';

export const EventUserDetail = () => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const eventData = location.state?.eventData;
    const [eventDetail, setEventDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageUrl, setImageUrl] = useState(null);
    const [organizationRankings, setOrganizationRankings] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [organizationResults, setOrganizationResults] = useState([]);
    const [organizationTotalPoint, setOrganizationTotalPoint] = useState(0);
    const [organizationStatistics, setOrganizationStatistics] = useState([]); // 기관별 통계 정보
    const [lotteryResults, setLotteryResults] = useState([]); // 추첨제 당첨자
    const [resultsLoading, setResultsLoading] = useState(false);
    const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
    const [hasMoreResults, setHasMoreResults] = useState(true);
    const [resultsOffset, setResultsOffset] = useState(0);
    const resultsOffsetRef = useRef(0);
    const tableRef = useRef(null);
    const resultsTableRef = useRef(null);
    const scrollContainerRef = useRef(null);

    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // 이벤트 데이터가 없으면 목록으로 리다이렉트
    useEffect(() => {
        if (!eventData) {
            navigate('/events/user', { replace: true });
            return;
        }
    }, [eventData, navigate]);

    // 페이지 로드 시 스크롤을 맨 위로 초기화
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [eventData?.id]);

    // 이벤트 상세 정보 가져오기
    useEffect(() => {
        const fetchEventDetail = async () => {
            if (!eventData?.id) return;

            try {
                setLoading(true);

                // 이벤트 상세 정보 가져오기
                const eventResponse = await eventAPI.getEvents({
                    id: eventData.id,
                    expand: 'user',
                });
                const events = eventResponse?.data?.events || eventResponse?.events || [];
                const event = events[0] || eventResponse?.data?.event || eventResponse?.event;

                if (event) {
                    setEventDetail(event);
                    setImageUrl(event.file || null);
                }
            } catch (err) {
                console.error('Failed to fetch event detail:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetail();
        // eventData 객체 대신 id만 의존 (참조 변경 시 반복 호출 방지)
    }, [eventData?.id]);

    // 이벤트 참여 데이터 가져오기
    const fetchParticipations = useCallback(async (reset = false) => {
        if (!eventData?.id) return;
        if (isLoadingMore && !reset) return;

        const currentOffset = reset ? 0 : offset;

        if (reset) {
            setIsLoadingMore(false);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const participationResponse = await eventAPI.getEventParticipations({
                eventId: eventData.id,
                expand: 'user',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
                state: '0,2',
            });

            const participationsData = participationResponse?.data?.eventParticipations || [];

            // 배열인지 확인
            const safeParticipationsData = Array.isArray(participationsData) ? participationsData : [];

            const total = participationResponse?.data?.total || 0;

            let newParticipations;
            if (reset) {
                newParticipations = safeParticipationsData;
                setOffset(safeParticipationsData.length);
            } else {
                newParticipations = [...safeParticipationsData];
                setOffset(prev => prev + safeParticipationsData.length);
            }

            setHasMore(newParticipations.length < total);

            // API에서 이미 랭킹순으로 정렬되어 오므로, 순서대로 숫자만 매김
            if (eventDetail?.type === 1) {
                // 랭킹제: 기관별로 그룹화하여 총 응모 포인트 계산 (정렬 없이 순서대로)
                const orgMap = new Map();
                newParticipations.forEach(participation => {
                    const orgId = participation.organizationId;
                    const orgName = participation.organization?.name || participation.organizationName || t('no_affiliation');
                    const userName = participation.user?.name || t('no_user');
                    const usedPoint = participation.usedPoint || 0;

                    if (!orgMap.has(orgId)) {
                        orgMap.set(orgId, {
                            organizationId: orgId,
                            organizationName: orgName,
                            totalPoint: 0,
                            topUser: userName,
                            topUserPoint: usedPoint,
                        });
                    }

                    const orgData = orgMap.get(orgId);
                    orgData.totalPoint += usedPoint;

                    // 기관 내 1위 사용자 업데이트
                    if (usedPoint > orgData.topUserPoint) {
                        orgData.topUser = userName;
                        orgData.topUserPoint = usedPoint;
                    }
                });

                // 기관별 데이터를 배열로 변환 (정렬 없이 순서대로)
                const orgRankings = Array.from(orgMap.values())
                    .map((org, index) => ({
                        rank: index + 1,
                        organizationId: org.organizationId,
                        organizationName: org.organizationName,
                        topUser: org.topUser,
                        totalPoint: org.totalPoint,
                    }));

                if (reset) {
                    setOrganizationRankings(orgRankings);
                } else {
                    setOrganizationRankings(prev => {
                        // 기존 랭킹에 추가 (중복 제거)
                        const merged = [...prev];
                        orgRankings.forEach(newOrg => {
                            const existingIndex = merged.findIndex(
                                o => o.organizationId === newOrg.organizationId
                            );
                            if (existingIndex >= 0) {
                                merged[existingIndex] = newOrg;
                            } else {
                                merged.push(newOrg);
                            }
                        });
                        // 순서대로 rank 재설정
                        return merged.map((org, index) => ({ ...org, rank: index + 1 }));
                    });
                }
            } else {
                // 추첨제: 개인별 참여 데이터를 랭킹 형식으로 변환 (정렬 없이 순서대로)
                const individualRankings = newParticipations.map((participation, index) => ({
                    rank: index + 1,
                    userId: participation.userId,
                    userName: participation.user?.name || t('no_user'),
                    organizationName: participation.organizationId === 0
                        ? (participation.user?.mainOrganizationName || t('no_affiliation'))
                        : (participation.organization?.name || participation.organizationName || t('no_affiliation')),
                    totalPoint: participation.usedPoint || 0,
                }));

                if (reset) {
                    setOrganizationRankings(individualRankings);
                } else {
                    setOrganizationRankings(prev => {
                        // 기존 랭킹에 추가 (중복 제거)
                        const merged = [...prev];
                        individualRankings.forEach(newItem => {
                            const existingIndex = merged.findIndex(
                                item => item.userId === newItem.userId
                            );
                            if (existingIndex >= 0) {
                                merged[existingIndex] = newItem;
                            } else {
                                merged.push(newItem);
                            }
                        });
                        // 순서대로 rank 재설정
                        return merged.map((item, index) => ({ ...item, rank: index + 1 }));
                    });
                }
            }
        } catch (err) {
            console.error('Failed to fetch participations:', err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [eventData?.id, eventDetail?.type, offset, isLoadingMore]);

    // 결과 데이터 가져오기 (state=2) - 무한스크롤
    const fetchResults = useCallback(async (reset = false) => {
        if (!eventData?.id || !eventDetail) return;
        if (isLoadingMoreResults && !reset) return;

        // 이벤트가 완료되었는지 확인 (status 값으로 판단)
        const isCompleted = eventDetail.status === 'finishing';
        if (!isCompleted) return;

        const currentOffset = reset ? 0 : resultsOffsetRef.current;

        if (reset) {
            setResultsLoading(true);
        } else {
            setIsLoadingMoreResults(true);
        }

        try {
            const resultsResponse = await eventAPI.getEventParticipations({
                eventId: eventData.id,
                expand: 'user',
                state: 2, // 결과 데이터만 조회
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
            });

            const resultsData = Array.isArray(resultsResponse?.data?.eventParticipations)
                ? resultsResponse.data.eventParticipations
                : Array.isArray(resultsResponse?.eventParticipations)
                    ? resultsResponse.eventParticipations
                    : Array.isArray(resultsResponse?.data)
                        ? resultsResponse.data
                        : [];

            const total = resultsResponse?.data?.total || resultsResponse?.total || 0;

            if (eventDetail?.type === 0) {
                // 추첨제: 개인별 당첨자
                if (reset) {
                    const lotteryWinners = resultsData.map((w, index) => ({
                        no: index + 1,
                        raw: w, // 원본 데이터 보관
                        userId: w.userId,
                        userName: w.user?.name || t('no_user'),
                        organizationName: w.organizationId === 0
                            ? (w.user?.mainOrganizationName || t('no_affiliation'))
                            : (w.organization?.name || w.organizationName || t('no_affiliation')),
                        usedPoint: w.usedPoint || 0,
                    }));
                    setLotteryResults(lotteryWinners);
                    setResultsOffset(resultsData.length);
                    resultsOffsetRef.current = resultsData.length;
                } else {
                    setLotteryResults(prev => {
                        const newWinners = resultsData.map((w, index) => ({
                            no: prev.length + index + 1,
                            raw: w,
                            userId: w.userId,
                            userName: w.user?.name || t('no_user'),
                            organizationName: w.organizationId === 0
                                ? (w.user?.mainOrganizationName || t('no_affiliation'))
                                : (w.organization?.name || w.organizationName || t('no_affiliation')),
                            usedPoint: w.usedPoint || 0,
                        }));
                        const combined = [...prev, ...newWinners];
                        // 중복 제거
                        const unique = combined.filter(
                            (item, index, self) =>
                                index === self.findIndex(p => p.userId === item.userId)
                        );
                        // no 재설정
                        return unique.map((item, index) => ({ ...item, no: index + 1 }));
                    });
                    const newOffset = currentOffset + resultsData.length;
                    setResultsOffset(newOffset);
                    resultsOffsetRef.current = newOffset;
                }
            } else if (eventDetail?.type === 1) {
                // 랭킹제: 기관별로 그룹화
                if (reset) {
                    // 기관별로 그룹화
                    const orgMap = new Map();
                    resultsData.forEach((p) => {
                        const orgId = p.organizationId || 0;
                        const orgName = p.organization?.name || p.organizationName || t('no_affiliation');

                        if (!orgMap.has(orgId)) {
                            orgMap.set(orgId, {
                                organizationId: orgId,
                                organizationName: orgName,
                                results: [],
                            });
                        }

                        orgMap.get(orgId).results.push({
                            userId: p.userId,
                            userName: p.user?.name || t('no_user'),
                            organizationName: orgName,
                            usedPoint: p.usedPoint || 0,
                            raw: p, // 원본 데이터 보관
                        });
                    });

                    // 각 기관별로 정렬 및 번호 매기기
                    const orgGroups = Array.from(orgMap.values()).map(org => {
                        const sortedResults = org.results
                            .sort((a, b) => (b.usedPoint || 0) - (a.usedPoint || 0))
                            .map((result, index) => ({
                                ...result,
                                no: index + 1,
                            }));

                        return {
                            ...org,
                            results: sortedResults,
                        };
                    });

                    setOrganizationResults(orgGroups);

                    // 각 기관별 통계 정보 가져오기 (API_PAGE_SIZE씩 배치)
                    const STATS_BATCH_SIZE = API_PAGE_SIZE;
                    const statisticsResults = [];
                    for (let i = 0; i < orgGroups.length; i += STATS_BATCH_SIZE) {
                        const batch = orgGroups.slice(i, i + STATS_BATCH_SIZE);
                        const batchPromises = batch.map(async (org) => {
                            try {
                                const statsResponse = await eventAPI.getEventParticipationsStatistics({
                                    eventId: eventData.id,
                                    organizationId: org.organizationId,
                                });

                                return {
                                    organizationId: org.organizationId,
                                    organizationName: org.organizationName,
                                    statistics: statsResponse?.data || [],
                                };
                            } catch (err) {
                                console.error(`Failed to fetch statistics for organization ${org.organizationId}:`, err);
                                return {
                                    organizationId: org.organizationId,
                                    organizationName: org.organizationName,
                                    statistics: {},
                                };
                            }
                        });
                        const batchResults = await Promise.all(batchPromises);
                        statisticsResults.push(...batchResults);
                    }
                    setOrganizationStatistics(statisticsResults);

                    setResultsOffset(resultsData.length);
                    resultsOffsetRef.current = resultsData.length;
                } else {
                    // 추가 데이터 로드 시 기존 구조 유지하면서 업데이트
                    setOrganizationResults(prev => {
                        const orgMap = new Map();

                        // 기존 데이터를 맵에 추가
                        prev.forEach(org => {
                            orgMap.set(org.organizationId, {
                                ...org,
                                results: [...org.results],
                            });
                        });

                        // 새 데이터를 기관별로 그룹화하여 추가
                        resultsData.forEach((p) => {
                            const orgId = p.organizationId || 0;
                            const orgName = p.organization?.name || p.organizationName || t('no_affiliation');

                            if (!orgMap.has(orgId)) {
                                orgMap.set(orgId, {
                                    organizationId: orgId,
                                    organizationName: orgName,
                                    results: [],
                                });
                            }

                            // 중복 체크
                            const existingResult = orgMap.get(orgId).results.find(
                                r => r.userId === p.userId
                            );

                            if (!existingResult) {
                                orgMap.get(orgId).results.push({
                                    userId: p.userId,
                                    userName: p.user?.name || t('no_user'),
                                    organizationName: orgName,
                                    usedPoint: p.usedPoint || 0,
                                    raw: p,
                                });
                            }
                        });

                        // 각 기관별로 정렬 및 번호 재설정
                        const updatedGroups = Array.from(orgMap.values()).map(org => {
                            const sortedResults = org.results
                                .sort((a, b) => (b.usedPoint || 0) - (a.usedPoint || 0))
                                .map((result, index) => ({
                                    ...result,
                                    no: index + 1,
                                }));

                            return {
                                ...org,
                                results: sortedResults,
                            };
                        });

                        return updatedGroups;
                    });

                    const newOffset = currentOffset + resultsData.length;
                    setResultsOffset(newOffset);
                    resultsOffsetRef.current = newOffset;
                }
            }

            // 더 불러올 데이터가 있는지 확인
            const finalOffset = reset ? resultsData.length : currentOffset + resultsData.length;
            setHasMoreResults(finalOffset < total && resultsData.length === ITEMS_PER_PAGE);
        } catch (err) {
            console.error('Failed to fetch results:', err);
        } finally {
            if (reset) {
                setResultsLoading(false);
            }
            setIsLoadingMoreResults(false);
        }
    }, [eventData?.id, eventDetail?.id, eventDetail?.status, eventDetail?.type, isLoadingMoreResults]);

    // 초기 참여 데이터 로드 (eventDetail은 state라 참조 변경 시 1회만 반응, fetchParticipations는 의존성에서 제외)
    useEffect(() => {
        if (eventData?.id && eventDetail) {
            setOffset(0);
            setHasMore(true);
            fetchParticipations(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- eventDetail?.id 변경 시에만 재실행
    }, [eventData?.id, eventDetail?.id]);

    // 이벤트가 완료되었을 때 결과 데이터 로드 (fetchResults는 useCallback 재생성 시 반복 호출되므로 의존성에서 제외)
    useEffect(() => {
        if (eventDetail) {
            setResultsOffset(0);
            resultsOffsetRef.current = 0;
            setHasMoreResults(true);
            setLotteryResults([]);
            setOrganizationResults([]);
            setOrganizationStatistics([]);
            fetchResults(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- eventDetail 로드/완료 상태(status) 변경 시에만 재실행
    }, [eventDetail?.id, eventDetail?.status]);

    // 무한 스크롤 핸들러 (랭킹 테이블용)
    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore) {
            fetchParticipations(false);
        }
    }, [hasMore, isLoadingMore, fetchParticipations]);

    // 결과 테이블 무한 스크롤 핸들러
    const handleResultsScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMoreResults && !isLoadingMoreResults && !resultsLoading) {
            fetchResults(false);
        }
    }, [hasMoreResults, isLoadingMoreResults, resultsLoading, fetchResults]);

    // 이벤트 상태 확인 (status 값으로 판단)
    const getEventStatus = () => {
        if (!eventDetail) return '';

        const status = eventDetail.status;

        if (status === 'waiting') {
            return t('application_has_not_started_yet');
        } else if (status === 'finishing') {
            return t('application_has_ended');
        } else if (status === 'ongoing') {
            const now = dayjs();
            const endDate = eventDetail.endDate ? dayjs(eventDetail.endDate) : null;
            if (endDate) {
                const daysLeft = endDate.diff(now, 'day');
                return t('application_is_ongoing') + ` (D-${daysLeft})`;
            }
            return t('application_is_ongoing');
        }

        return t('application_is_ongoing');
    };

    if (!eventData || loading) {
        return (
            <ContentArea>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                        로딩 중...
                    </Typography>
                </Box>
            </ContentArea>
        );
    }

    const eventStatus = getEventStatus();
    const endDate = eventDetail?.endDate || eventData?.endDate;
    const daysLeft = endDate ? dayjs(endDate).diff(dayjs(), 'day') : null;
    const isEventCompleted = eventDetail?.status === 'finishing'; // 이벤트 완료 여부 (status 값으로 판단)
    const shouldHideRanking = daysLeft !== null && daysLeft <= 3 && !isEventCompleted; // 마감 3일 전까지는 랭킹 테이블 숨김
    const isApplyableEvent = eventDetail?.type === 0 || eventDetail?.type === 1; // 추첨제 또는 랭킹제

    return (
        <ContentArea sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <Box
                ref={scrollContainerRef}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    px: 1.25,
                    py: 1.25,
                    gap: 1.25,
                    overflowY: 'auto',
                    pb: '80px',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {/* 이벤트 개요 테이블 */}
                <TableContainer
                    component={Paper}
                    sx={{
                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                        borderRadius: '10px',
                        border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                        width: '100%',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Table size="small" sx={{ width: '100%', minWidth: '100%' }}>
                        <colgroup>
                            <col style={{ width: 'auto', minWidth: '120px', maxWidth: '40%' }} />
                            <col style={{ width: 'auto', minWidth: '150px' }} />
                        </colgroup>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '16px',
                                    verticalAlign: 'top',
                                }}>
                                    {t('event_name')}
                                </TableCell>
                                <TableCell sx={{
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    verticalAlign: 'top',
                                }}>
                                    {eventDetail?.name || eventData?.name || '-'}
                                </TableCell>
                            </TableRow>
                            {/* 당첨자(기관)수 - 추첨제/랭킹제인 경우에만 표시 */}
                            {isApplyableEvent && (
                                <TableRow>
                                    <TableCell sx={{
                                        borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                        color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                        whiteSpace: 'nowrap',
                                        paddingRight: '16px',
                                        verticalAlign: 'top',
                                    }}>
                                        {t('event_winner_count')}
                                    </TableCell>
                                    <TableCell sx={{
                                        borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                        color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        verticalAlign: 'top',
                                    }}>
                                        {eventDetail?.targetCount || '-'}
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell sx={{
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '16px',
                                    verticalAlign: 'top',
                                }}>
                                    {t('application_start_date')}
                                </TableCell>
                                <TableCell sx={{
                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    verticalAlign: 'top',
                                }}>
                                    {eventDetail?.startDate ? formatDateTimeDisplay(eventDetail.startDate, 'datetime') : '-'}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    whiteSpace: 'nowrap',
                                    paddingRight: '16px',
                                    verticalAlign: 'top',
                                }}>
                                    {t('application_end_date')}
                                </TableCell>
                                <TableCell sx={{
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    verticalAlign: 'top',
                                }}>
                                    {eventDetail?.endDate ? formatDateTimeDisplay(eventDetail.endDate, 'datetime') : '-'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 이벤트 이미지 */}
                {imageUrl && (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt="Event"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: '10px',
                            flexShrink: 0,
                            objectFit: 'contain',
                            position: 'relative',
                            display: 'block',
                        }}
                    />
                )}

                {/* 추첨제/랭킹제인 경우에만 이미지 아래 내용 표시 */}
                {isApplyableEvent && (
                    <>
                        {/* 총응모포인트 - D-3 이내에는 숨김 */}
                        {!shouldHideRanking && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                    borderRadius: '10px',
                                    minHeight: '48px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    mt: 1.25,
                                    mb: 1.25,
                                    border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                    position: 'relative',
                                    zIndex: 0,
                                }}
                            >
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    flexShrink: 0,
                                }}>
                                    {t('total_application_point')}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    flexShrink: 0,
                                }}>
                                    {eventDetail?.totalParticipationUsedPoint?.toLocaleString() || 0}
                                </Typography>
                            </Box>
                        )}


                        {/* 랭킹 테이블 - D-3 이내에는 숨김 */}
                        {!shouldHideRanking && (
                            <Box sx={{
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                overflow: 'visible',
                                mb: 1.25,
                                flexShrink: 0,
                                position: 'relative',
                                zIndex: 2,
                                minHeight: '200px',
                            }}>
                                <TableContainer
                                    component={Paper}
                                    ref={tableRef}
                                    onScroll={handleScroll}
                                    sx={{
                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                        borderRadius: '10px',
                                        maxHeight: '400px',
                                        minHeight: '200px',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        border: `1px solid ${theme.palette.figma?.white?.[20] || 'rgba(255, 255, 255, 0.2)'}`,
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        position: 'relative',
                                        zIndex: 2,
                                        '& .MuiTable-root': {
                                            backgroundColor: 'transparent',
                                            width: '100% !important',
                                            maxWidth: '100% !important',
                                            minWidth: 0,
                                            tableLayout: 'auto',
                                            boxSizing: 'border-box',
                                        },
                                        '& .MuiTableCell-root': {
                                            boxSizing: 'border-box',
                                            padding: '8px 4px !important',
                                        },
                                        '& .MuiTableHead-root': {
                                            backgroundColor: 'transparent',
                                        },
                                        '& .MuiTableBody-root': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <Table
                                        size="small"
                                        sx={{
                                            width: '100% !important',
                                            maxWidth: '100% !important',
                                            minWidth: 0,
                                            tableLayout: 'auto',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    fontWeight: 500,
                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                    padding: '8px 4px',
                                                    // width: '8%',
                                                    //    maxWidth: '8%',
                                                    boxSizing: 'border-box',
                                                }}>
                                                    Rank
                                                </TableCell>
                                                {eventDetail?.type === 1 ? (
                                                    <>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            //  width: '22%',
                                                            //  maxWidth: '22%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('org_top_rank')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            // width: '35%',
                                                            //  maxWidth: '35%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('main_organization_name')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            textAlign: 'right',
                                                            //  width: '35%',
                                                            //    maxWidth: '35%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('org_total_application')}
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            //  width: '22%',
                                                            //  maxWidth: '22%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('name')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            //   width: '35%',
                                                            // maxWidth: '35%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('main_organization_name')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            fontWeight: 500,
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            textAlign: 'right',
                                                            //  width: '35%',
                                                            //   maxWidth: '35%',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            boxSizing: 'border-box',
                                                        }}>
                                                            {t('application_point')}
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {organizationRankings.length > 0 ? (
                                                organizationRankings.map((item, index) => (
                                                    <TableRow key={eventDetail?.type === 1 ? (item.organizationId || index) : (item.userId || index)}>
                                                        <TableCell sx={{
                                                            color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                            borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            padding: '8px 4px',
                                                            // width: '8%',
                                                            // maxWidth: '8%',
                                                            boxSizing: 'border-box',
                                                            textAlign: 'right',
                                                        }}>
                                                            {item.rank}
                                                        </TableCell>
                                                        {eventDetail?.type === 1 ? (
                                                            <>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    // width: '22%',
                                                                    // maxWidth: '22%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.topUser || '-'}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    // width: '35%',
                                                                    // maxWidth: '35%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.organizationName || '-'}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    textAlign: 'right',
                                                                    // width: '35%',
                                                                    // maxWidth: '35%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.totalPoint?.toLocaleString() || 0}
                                                                </TableCell>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    // width: '22%',
                                                                    // maxWidth: '22%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.userName || '-'}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    // width: '35%',
                                                                    // maxWidth: '35%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.organizationName || '-'}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '8px 4px',
                                                                    textAlign: 'right',
                                                                    // width: '35%',
                                                                    // maxWidth: '35%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    {item.totalPoint?.toLocaleString() || 0}
                                                                </TableCell>
                                                            </>
                                                        )}
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        sx={{
                                                            textAlign: 'center',
                                                            color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                            borderBottom: 'none',
                                                        }}
                                                    >
                                                        참여 데이터가 없습니다.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {/* D-3 이내일 때 안내 메시지 */}
                        {shouldHideRanking && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                padding: '20px',
                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                borderRadius: '10px',
                                border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                mb: 1.25,
                                position: 'relative',
                                zIndex: 0,
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    textAlign: 'center',
                                    fontWeight: 500,
                                }}>
                                    {eventStatus}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    textAlign: 'center',
                                }}>
                                    {t('ranking_cannot_be_viewed_after_3_days_before_deadline')}
                                </Typography>
                            </Box>
                        )}

                        {/* 상태 메시지 또는 결과 */}
                        {isEventCompleted ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: 1 }}>
                                {/* 랭킹제 결과 */}
                                {eventDetail?.type === 1 && (
                                    <>
                                        <Typography sx={{
                                            fontSize: '18px',
                                            fontWeight: 700,
                                            color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                            textAlign: 'left',
                                        }}>
                                            {t('result')}
                                        </Typography>

                                        {resultsLoading ? (
                                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
                                                {t('loading_data')}
                                            </Typography>
                                        ) : organizationResults.length > 0 ? (
                                            <>
                                                {/* 기관별로 반복 표시 */}
                                                {organizationResults.map((orgGroup, orgIndex) => {
                                                    // 해당 기관의 통계 정보 찾기
                                                    const orgStats = organizationStatistics.find(
                                                        s => s.organizationId === orgGroup.organizationId
                                                    );

                                                    return (
                                                        <Box key={orgGroup.organizationId || orgIndex} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                            {/* 기관별 요약 바 */}
                                                            <Box sx={{
                                                                display: 'flex',
                                                                width: '100%',
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                            }}>
                                                                <Box sx={{
                                                                    backgroundColor: theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                                                                    padding: '12px 16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    minWidth: '150px',
                                                                }}>
                                                                    <Typography sx={{
                                                                        fontSize: '14px',
                                                                        color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                        fontWeight: 500,
                                                                    }}>
                                                                        {orgGroup.organizationName || '-'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{
                                                                    backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                    padding: '12px 16px',
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'flex-end',
                                                                }}>
                                                                    <Typography sx={{
                                                                        fontSize: '14px',
                                                                        color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                        fontWeight: 700,
                                                                    }}>
                                                                        {orgStats.statistics.eventParticipationsStatistics[0].organizationTotalUsedPoint.toLocaleString()}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>

                                                            {/* 결과 테이블 */}
                                                            <TableContainer
                                                                component={Paper}
                                                                ref={orgIndex === 0 ? resultsTableRef : null}
                                                                onScroll={orgIndex === 0 ? handleResultsScroll : undefined}
                                                                sx={{
                                                                    backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                    borderRadius: '10px',
                                                                    border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    maxHeight: '300px',
                                                                    overflowX: 'hidden',
                                                                    overflowY: 'auto',
                                                                    width: '100%',
                                                                    maxWidth: '100%',
                                                                    boxSizing: 'border-box',
                                                                    '& .MuiTable-root': {
                                                                        width: '100% !important',
                                                                        maxWidth: '100% !important',
                                                                        minWidth: 0,
                                                                        tableLayout: 'fixed',
                                                                        boxSizing: 'border-box',
                                                                    },
                                                                }}
                                                            >
                                                                <Table size="small" stickyHeader sx={{
                                                                    width: '100% !important',
                                                                    maxWidth: '100% !important',
                                                                    minWidth: 0,
                                                                    tableLayout: 'fixed',
                                                                    boxSizing: 'border-box',
                                                                }}>
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                                fontWeight: 500,
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                                padding: '6px 4px',
                                                                                width: '10%',
                                                                                maxWidth: '10%',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                no
                                                                            </TableCell>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                                fontWeight: 500,
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                                padding: '6px 4px',
                                                                                width: '25%',
                                                                                maxWidth: '25%',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                {t('name')}
                                                                            </TableCell>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                                fontWeight: 500,
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                                padding: '6px 4px',
                                                                                width: '35%',
                                                                                maxWidth: '35%',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'break-word',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                {t('main_organization_name')}
                                                                            </TableCell>
                                                                            <TableCell sx={{
                                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                                fontWeight: 500,
                                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                                textAlign: 'right',
                                                                                padding: '6px 4px',
                                                                                width: '30%',
                                                                                maxWidth: '30%',
                                                                                boxSizing: 'border-box',
                                                                                overflow: 'hidden',
                                                                            }}>
                                                                                {t('application_point')}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {orgGroup.results.map((result, index) => (
                                                                            <TableRow key={result.userId || index}>
                                                                                <TableCell sx={{
                                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                    padding: '6px 4px',
                                                                                    width: '10%',
                                                                                    maxWidth: '10%',
                                                                                    boxSizing: 'border-box',
                                                                                    overflow: 'hidden',
                                                                                }}>
                                                                                    {result.no}
                                                                                </TableCell>
                                                                                <TableCell sx={{
                                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                    padding: '6px 4px',
                                                                                    width: '25%',
                                                                                    maxWidth: '25%',
                                                                                    wordBreak: 'break-word',
                                                                                    overflowWrap: 'break-word',
                                                                                    boxSizing: 'border-box',
                                                                                    overflow: 'hidden',
                                                                                }}>
                                                                                    {result.userName}
                                                                                </TableCell>
                                                                                <TableCell sx={{
                                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                    padding: '6px 4px',
                                                                                    width: '35%',
                                                                                    maxWidth: '35%',
                                                                                    wordBreak: 'break-word',
                                                                                    overflowWrap: 'break-word',
                                                                                    boxSizing: 'border-box',
                                                                                    overflow: 'hidden',
                                                                                }}>
                                                                                    {result.organizationName}
                                                                                </TableCell>
                                                                                <TableCell sx={{
                                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                                    textAlign: 'right',
                                                                                    padding: '6px 4px',
                                                                                    width: '30%',
                                                                                    maxWidth: '30%',
                                                                                    boxSizing: 'border-box',
                                                                                    overflow: 'hidden',
                                                                                }}>
                                                                                    {result.usedPoint.toLocaleString()}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                        {isLoadingMoreResults && orgIndex === 0 && (
                                                                            <TableRow>
                                                                                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                                                    <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                                                                                        로딩 중...
                                                                                    </Typography>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Box>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
                                                결과 데이터가 없습니다.
                                            </Typography>
                                        )}
                                    </>
                                )}

                                {/* 추첨제 결과 */}
                                {eventDetail?.type === 0 && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                            <Typography sx={{
                                                fontSize: '18px',
                                                fontWeight: 700,
                                                color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                textAlign: 'left',
                                            }}>
                                                {t('lottery_result')}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '12px',
                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                            }}>
                                                {t('result_notified_after_deadline')}
                                            </Typography>
                                        </Box>

                                        {resultsLoading ? (
                                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
                                                {t('loading_data')}
                                            </Typography>
                                        ) : lotteryResults.length > 0 ? (
                                            <TableContainer
                                                component={Paper}
                                                onScroll={handleResultsScroll}
                                                sx={{
                                                    backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                    borderRadius: '10px',
                                                    border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                    maxHeight: '300px',
                                                    overflowX: 'hidden',
                                                    overflowY: 'auto',
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    boxSizing: 'border-box',
                                                    '& .MuiTable-root': {
                                                        width: '100% !important',
                                                        maxWidth: '100% !important',
                                                        minWidth: 0,
                                                        tableLayout: 'fixed',
                                                        boxSizing: 'border-box',
                                                    },
                                                }}
                                            >
                                                <Table size="small" stickyHeader sx={{
                                                    width: '100% !important',
                                                    maxWidth: '100% !important',
                                                    minWidth: 0,
                                                    tableLayout: 'fixed',
                                                    boxSizing: 'border-box',
                                                }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{
                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                fontWeight: 500,
                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                padding: '6px 4px',
                                                                width: '10%',
                                                                maxWidth: '10%',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                            }}>
                                                                no
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                fontWeight: 500,
                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                padding: '6px 4px',
                                                                width: '25%',
                                                                maxWidth: '25%',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                            }}>
                                                                {t('name')}
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                fontWeight: 500,
                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                padding: '6px 4px',
                                                                width: '35%',
                                                                maxWidth: '35%',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                            }}>
                                                                {t('main_organization_name')}
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                                fontWeight: 500,
                                                                borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                                textAlign: 'right',
                                                                padding: '6px 4px',
                                                                width: '30%',
                                                                maxWidth: '30%',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                            }}>
                                                                {t('application_point')}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {lotteryResults.map((result, index) => (
                                                            <TableRow key={result.userId || index}>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '6px 4px',
                                                                    width: '10%',
                                                                    maxWidth: '10%',
                                                                    boxSizing: 'border-box',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    {result.no}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '6px 4px',
                                                                    width: '25%',
                                                                    maxWidth: '25%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    {result.userName}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    padding: '6px 4px',
                                                                    width: '35%',
                                                                    maxWidth: '35%',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    boxSizing: 'border-box',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    {result.organizationName}
                                                                </TableCell>
                                                                <TableCell sx={{
                                                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                                                    borderBottom: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                                    textAlign: 'right',
                                                                    padding: '6px 4px',
                                                                    width: '30%',
                                                                    maxWidth: '30%',
                                                                    boxSizing: 'border-box',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    {result.usedPoint.toLocaleString()}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                        {isLoadingMoreResults && (
                                                            <TableRow>
                                                                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                                    <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                                                                        로딩 중...
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
                                                결과 데이터가 없습니다.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        ) : !shouldHideRanking && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                mt: 2.5,
                                mb: 1,
                                position: 'relative',
                                zIndex: 0,
                                flexShrink: 0,
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    color: theme.palette.figma?.white?.[100] || theme.palette.figma?.buttonText || '#ffffff',
                                    textAlign: 'center'
                                }}>
                                    {eventStatus}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </ContentArea>
    );
};

export default EventUserDetail;
